import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { ViewMode } from "../components/shared/financialPeriodConfig";

dayjs.extend(quarterOfYear);

export interface TransformedDataPoint {
  period: string;
  date: string;
  year: number;
  quarter: number | null;
  [key: string]: any;
}

export const transformFinancialData = <T extends { date: string }>(
  data: T[],
  viewMode: ViewMode,
  valueKey: string,
  getValueFn: (item: T) => number | null,
  divisor: number = 1000000
): TransformedDataPoint[] => {
  const validData = data.filter((item) => getValueFn(item) !== null && getValueFn(item) !== undefined);

  if (viewMode === "quarterly") {
    const sortedData = validData.sort(
      (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
    );

    const periodCounts = new Map<string, number>();

    return sortedData.map((item) => {
      const basePeriod = `Q${dayjs(item.date).quarter()} ${dayjs(item.date).year()}`;
      const count = periodCounts.get(basePeriod) ?? 0;
      periodCounts.set(basePeriod, count + 1);
      const period = count > 0 ? `${basePeriod} (${count + 1})` : basePeriod;

      return {
        period,
        [valueKey]: getValueFn(item)! / divisor,
        date: item.date,
        year: dayjs(item.date).year(),
        quarter: dayjs(item.date).quarter(),
      };
    });
  } else {
    return validData
      .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix())
      .map((item) => ({
        period: dayjs(item.date).year().toString(),
        [valueKey]: getValueFn(item)! / divisor,
        date: item.date,
        year: dayjs(item.date).year(),
        quarter: null,
      }));
  }
};

export interface GrowthMetric {
  label: string;
  value: number;
}

export const calculateQuarterlyGrowthMetrics = (
  chartData: TransformedDataPoint[],
  valueKey: string
): GrowthMetric[] => {
  if (chartData.length === 0) return [];

  const earliestDate = dayjs(chartData[0].date);
  const now = dayjs();
  const spanInMonths = now.diff(earliestDate, "month");

  const oneYearAgo = dayjs().subtract(1, "year");
  const threeYearsAgo = dayjs().subtract(3, "years");
  const fiveYearsAgo = dayjs().subtract(5, "years");

  const calculateAvgQoQ = (filteredData: TransformedDataPoint[]) => {
    const growthRates = filteredData
      .map((item, index) => {
        if (index === 0) return null;
        const prev = filteredData[index - 1][valueKey] as number;
        return prev !== 0
          ? ((item[valueKey] as number) - prev) / Math.abs(prev) * 100
          : 0;
      })
      .filter((g): g is number => g !== null);

    return growthRates.length > 0
      ? growthRates.reduce((a, b) => a + b, 0) / growthRates.length
      : 0;
  };

  const metrics: GrowthMetric[] = [];

  if (spanInMonths >= 10) {
    metrics.push({
      label: "Avg QoQ Growth (1Y)",
      value: calculateAvgQoQ(chartData.filter((item) => dayjs(item.date).isAfter(oneYearAgo))),
    });
  }

  if (spanInMonths >= 30) {
    metrics.push({
      label: "Avg QoQ Growth (3Y)",
      value: calculateAvgQoQ(chartData.filter((item) => dayjs(item.date).isAfter(threeYearsAgo))),
    });
  }

  if (spanInMonths >= 48) {
    metrics.push({
      label: "Avg QoQ Growth (5Y)",
      value: calculateAvgQoQ(chartData.filter((item) => dayjs(item.date).isAfter(fiveYearsAgo))),
    });
  }

  return metrics;
};

export const calculateAnnualGrowthMetrics = (
  chartData: TransformedDataPoint[],
  valueKey: string,
  handleNegatives: boolean = false
): GrowthMetric[] => {
  if (chartData.length < 2) return [];

  const calculateCAGR = (years: number) => {
    const targetIndex = Math.max(0, chartData.length - years - 1);
    const baseValue = chartData[targetIndex]?.[valueKey] as number ?? 0;
    const currentValue = chartData[chartData.length - 1]?.[valueKey] as number ?? 0;

    if (baseValue === 0 || years === 0) return 0;

    if (handleNegatives) {
      if (baseValue < 0 && currentValue > 0) {
        return Math.abs(currentValue / baseValue) * 100;
      } else if (baseValue > 0 && currentValue < 0) {
        return -Math.abs(currentValue / baseValue) * 100;
      } else if (baseValue > 0 && currentValue > 0) {
        return (Math.pow(currentValue / baseValue, 1 / years) - 1) * 100;
      } else {
        return (Math.pow(Math.abs(currentValue) / Math.abs(baseValue), 1 / years) - 1) * 100;
      }
    }

    return (Math.pow(currentValue / baseValue, 1 / years) - 1) * 100;
  };

  const metrics: GrowthMetric[] = [];

  if (chartData.length >= 2) {
    metrics.push({ label: "1Y Growth", value: calculateCAGR(1) });
  }

  if (chartData.length >= 4) {
    metrics.push({ label: "3Y CAGR", value: calculateCAGR(3) });
  }

  if (chartData.length >= 6) {
    metrics.push({ label: "5Y CAGR", value: calculateCAGR(5) });
  }

  if (chartData.length >= 11) {
    metrics.push({ label: "10Y CAGR", value: calculateCAGR(10) });
  }

  return metrics;
};
